import { colOf, colorOf, isKing, legalMoves, rowOf, sqAt } from '@billycheckers/shared';
import type { Cell, Color, Move, Position, Square } from '@billycheckers/shared';
import { click } from './sound.js';

/**
 * Доска: отрисовка, тап-тап-ввод, пошаговый ввод цепочки боя.
 * Ход отправляется наружу (onMove) только когда цепочка завершена.
 * Источник истины — сервер; доска лишь показывает и собирает намерение.
 */
export class BoardView {
  readonly el: HTMLElement;
  private cells: HTMLElement[] = [];
  private pieceEls = new Map<Square, HTMLElement>();
  private orientation: Color = 'white';
  private position: Position | null = null;
  private myColor: Color | null = null;
  private sel: Square | null = null;
  private chain: { from: Square; prefix: Square[]; candidates: Move[] } | null = null;
  private lastMove: Move | null = null;

  onMove: (m: Move) => void = () => {};
  onHint: (text: string) => void = () => {};

  constructor() {
    this.el = document.createElement('div');
    this.el.className = 'board';
    this.buildCells();
    this.el.addEventListener('click', (e) => this.onClick(e));
  }

  // ── геометрия ─────────────────────────────────────────────────────────────

  private disp(sq: Square): { dr: number; dc: number } {
    const r = rowOf(sq);
    const c = colOf(sq);
    return this.orientation === 'white' ? { dr: 7 - r, dc: c } : { dr: r, dc: 7 - c };
  }

  private cellAt(sq: Square): HTMLElement {
    const { dr, dc } = this.disp(sq);
    return this.cells[dr * 8 + dc]!;
  }

  private buildCells(): void {
    this.el.replaceChildren();
    this.cells = [];
    for (let dr = 0; dr < 8; dr++) {
      for (let dc = 0; dc < 8; dc++) {
        const r = this.orientation === 'white' ? 7 - dr : dr;
        const c = this.orientation === 'white' ? dc : 7 - dc;
        const sq = sqAt(c, r);
        const div = document.createElement('div');
        div.className = 'sq ' + (sq >= 0 ? 'd' : 'l');
        if (sq >= 0) div.dataset.sq = String(sq);
        this.el.appendChild(div);
        this.cells.push(div);
      }
    }
  }

  // ── полная перерисовка (room:state / resync) ──────────────────────────────

  setState(position: Position, myColor: Color | null, lastMove: Move | null): void {
    const wantOrientation: Color = myColor ?? 'white';
    if (wantOrientation !== this.orientation) {
      this.orientation = wantOrientation;
      this.buildCells();
    }
    this.position = position;
    this.myColor = myColor;
    this.lastMove = lastMove;
    this.sel = null;
    this.chain = null;
    for (const el of this.pieceEls.values()) el.remove();
    this.pieceEls.clear();
    for (let sq = 0 as Square; sq < 32; sq++) {
      const cell = position.board[sq] as Cell;
      if (cell === 0) continue;
      this.addPiece(sq, cell);
    }
    this.paintLastMove();
    this.clearHints();
  }

  private addPiece(sq: Square, cell: Cell): void {
    const el = document.createElement('div');
    el.className = 'p ' + (colorOf(cell) === 'white' ? 'w' : 'b');
    const disc = document.createElement('div');
    disc.className = 'disc';
    const edge = document.createElement('div');
    edge.className = 'edge';
    disc.appendChild(edge);
    el.appendChild(disc);
    if (isKing(cell)) {
      const crown = document.createElement('div');
      crown.className = 'crown';
      el.appendChild(crown);
    }
    this.place(el, sq);
    this.el.appendChild(el);
    this.pieceEls.set(sq, el);
  }

  private place(el: HTMLElement, sq: Square): void {
    const { dr, dc } = this.disp(sq);
    el.style.transform = `translate(${dc * 100}%, ${dr * 100}%)`;
  }

  // ── применение подтверждённого сервером хода ──────────────────────────────

  applyServerMove(move: Move, position: Position): void {
    const el = this.pieceEls.get(move.from);
    if (!el) {
      // рассинхрон — полная перерисовка
      this.setState(position, this.myColor, move);
      return;
    }
    const dest = move.path[move.path.length - 1]!;
    el.classList.add('moving');
    this.place(el, dest);
    window.setTimeout(() => el.classList.remove('moving'), 360);
    this.pieceEls.delete(move.from);
    // на поле dest никого быть не может, кроме самой фишки (цепочка может вернуться на from)
    this.pieceEls.set(dest, el);
    for (const capSq of move.captured) {
      const cap = this.pieceEls.get(capSq);
      if (cap) {
        cap.classList.remove('dead');
        cap.classList.add('dying');
        cap.style.transform += ' scale(.5)';
        window.setTimeout(() => cap.remove(), 320);
        this.pieceEls.delete(capSq);
      }
    }
    if (move.promoted && !el.querySelector('.crown')) {
      const crown = document.createElement('div');
      crown.className = 'crown';
      el.appendChild(crown);
      window.setTimeout(() => click(0.42), 170);
    }
    this.position = position;
    this.lastMove = move;
    this.sel = null;
    this.chain = null;
    this.paintLastMove();
    this.clearHints();
    click(move.captured.length > 0 ? 0.5 : 0.32);
  }

  // ── подсветка ─────────────────────────────────────────────────────────────

  private paintLastMove(): void {
    for (const c of this.cells) c.classList.remove('from', 'to');
    if (!this.lastMove) return;
    this.cellAt(this.lastMove.from).classList.add('from');
    this.cellAt(this.lastMove.path[this.lastMove.path.length - 1]!).classList.add('to');
  }

  private clearHints(): void {
    for (const h of this.el.querySelectorAll('.hint')) h.remove();
    for (const p of this.pieceEls.values()) p.classList.remove('sel');
  }

  private showHints(stops: Array<{ sq: Square; capture: boolean }>): void {
    for (const { sq, capture } of stops) {
      const h = document.createElement('div');
      h.className = 'hint' + (capture ? ' cap' : '');
      this.cellAt(sq).appendChild(h);
    }
  }

  // ── ввод ──────────────────────────────────────────────────────────────────

  private myMoves(): Move[] {
    if (!this.position || !this.myColor || this.position.turn !== this.myColor) return [];
    return legalMoves(this.position);
  }

  private onClick(e: MouseEvent): void {
    if (!this.position) return;
    const target = e.target as HTMLElement;
    let sq: Square | null = null;
    const pieceEl = target.closest('.p');
    if (pieceEl) {
      for (const [s, el] of this.pieceEls) {
        if (el === pieceEl) { sq = s; break; }
      }
    } else {
      const cellEl = target.closest<HTMLElement>('.sq');
      if (cellEl?.dataset.sq !== undefined) sq = Number(cellEl.dataset.sq);
    }
    if (sq === null) {
      // клик мимо игровых полей: снимаем выделение (цепочку не трогаем)
      if (!this.chain && this.sel !== null) {
        this.sel = null;
        this.clearHints();
      }
      return;
    }

    // идёт ввод цепочки — принимаем следующий шаг или отмену кликом по исходной фишке
    if (this.chain) {
      const nexts = this.chainNextStops();
      if (nexts.includes(sq)) {
        this.extendChain(sq);
      } else if (sq === this.chain.from) {
        this.cancelChain();
      }
      return;
    }

    const moves = this.myMoves();

    // клик по цели выбранной фишки
    if (this.sel !== null) {
      const fromSel = moves.filter((m) => m.from === this.sel);
      const quiet = fromSel.find((m) => m.captured.length === 0 && m.path[0] === sq);
      if (quiet) {
        this.clearHints();
        this.sel = null;
        this.onMove(quiet);
        return;
      }
      const caps = fromSel.filter((m) => m.captured.length > 0 && m.path[0] === sq);
      if (caps.length > 0) {
        this.chain = { from: this.sel, prefix: [], candidates: fromSel.filter((m) => m.captured.length > 0) };
        this.extendChain(sq);
        return;
      }
    }

    // выбор фишки
    const cell = this.position.board[sq] as Cell;
    if (cell !== 0 && colorOf(cell) === this.myColor) {
      if (this.sel === sq) {
        this.sel = null;
        this.clearHints();
        return;
      }
      const has = moves.some((m) => m.from === sq);
      if (!has) {
        if (moves.length > 0 && moves[0]!.captured.length > 0) this.onHint('нужно бить');
        return;
      }
      this.sel = sq;
      this.clearHints();
      this.pieceEls.get(sq)?.classList.add('sel');
      const stops = new Map<Square, boolean>();
      for (const m of moves.filter((mm) => mm.from === sq)) {
        const s = m.path[0]!;
        stops.set(s, (stops.get(s) ?? false) || m.captured.length > 0);
      }
      this.showHints([...stops.entries()].map(([s, capture]) => ({ sq: s, capture })));
      click(0.14);
      return;
    }

    // пустое место — снять выделение
    this.sel = null;
    this.clearHints();
  }

  /** Отмена начатой (ещё не отправленной) цепочки: фишка и жертвы возвращаются как были. */
  private cancelChain(): void {
    const chain = this.chain;
    if (!chain) return;
    const el = this.pieceEls.get(chain.from);
    if (el) {
      el.classList.add('moving');
      this.place(el, chain.from);
      window.setTimeout(() => el.classList.remove('moving'), 360);
    }
    for (const p of this.pieceEls.values()) p.classList.remove('dead');
    this.chain = null;
    this.sel = null;
    this.clearHints();
  }

  private chainNextStops(): Square[] {
    if (!this.chain) return [];
    const i = this.chain.prefix.length;
    const stops = new Set<Square>();
    for (const m of this.chain.candidates) {
      if (m.path.length > i) stops.add(m.path[i]!);
    }
    return [...stops];
  }

  private extendChain(stop: Square): void {
    const chain = this.chain!;
    chain.prefix.push(stop);
    const i = chain.prefix.length;
    chain.candidates = chain.candidates.filter(
      (m) => m.path.length >= i && chain.prefix.every((s, k) => m.path[k] === s),
    );
    // визуальный шаг: фишка едет, свежепобитая тускнеет
    const el = this.pieceEls.get(chain.from);
    if (el) {
      el.classList.add('moving');
      this.place(el, stop);
    }
    const sample = chain.candidates[0];
    if (sample) {
      const victim = sample.captured[i - 1];
      if (victim !== undefined) this.pieceEls.get(victim)?.classList.add('dead');
    }
    click(0.5);
    const complete = chain.candidates.find((m) => m.path.length === i);
    if (complete) {
      // завершённая цепочка не может иметь продолжений (правила гарантируют)
      this.chain = null;
      this.sel = null;
      this.clearHints();
      this.onMove(complete);
      return;
    }
    this.clearHints();
    this.pieceEls.get(chain.from)?.classList.add('sel');
    this.showHints(this.chainNextStops().map((s) => ({ sq: s, capture: true })));
  }
}
